import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../lib/api.js";
import type { GamesListResponse } from "../lib/types.js";
import type { AsyncResult } from "./useApi.js";

export interface GamesQuery {
  status?: string;
  grade?: number;
  topic?: string;
  limit?: number;
}

function buildQuery(q: GamesQuery): string {
  const parts: string[] = [];
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (typeof q.grade === "number") parts.push(`grade=${encodeURIComponent(String(q.grade))}`);
  if (q.topic) parts.push(`topic=${encodeURIComponent(q.topic)}`);
  if (typeof q.limit === "number") parts.push(`limit=${encodeURIComponent(String(q.limit))}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * Fetch the games catalog. Backed by TanStack Query under key `['games', ...]`
 * so landing, catalog, diagnostic, and any other consumer share one cache
 * entry per unique filter combination. 5-minute stale time: the catalog
 * changes infrequently and a slightly stale list is fine.
 *
 * `opts.initialData` lets a caller seed the first render from a persisted
 * snapshot (e.g. the landing page's localStorage cache of the last catalog).
 * It's marked immediately stale (`initialDataUpdatedAt: 0`) so the hook still
 * revalidates in the background — stale-while-revalidate — rather than trusting
 * the snapshot for the full `staleTime`. With a snapshot present `loading` is
 * false on the first render, so the UI paints real data instead of a skeleton.
 */
export function useGames(
  q: GamesQuery = {},
  opts: { initialData?: GamesListResponse } = {},
): AsyncResult<GamesListResponse> {
  const queryString = buildQuery(q);

  const query = useQuery<GamesListResponse>({
    queryKey: ["games", queryString],
    queryFn: () => apiFetch<GamesListResponse>(`/v1/games${queryString}`),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    initialData: opts.initialData,
    initialDataUpdatedAt: opts.initialData ? 0 : undefined,
  });

  return {
    data: query.data ?? null,
    err: query.error
      ? query.error instanceof ApiError
        ? `${query.error.status}: ${query.error.message}`
        : query.error.message
      : null,
    loading: query.isPending,
    reload: () => {
      void query.refetch();
    },
  };
}
